import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Button, Container, Grid, Group, Select, SimpleGrid, Stack, Text, Title } from "@mantine/core"
import { IconAlertTriangle, IconRefresh, IconShoppingCart } from "@tabler/icons-react"
import { EmptyState } from "../../../../components/EmptyState"
import { KpiRowSkeleton, PageHeaderSkeleton, TableSkeleton } from "../../../../components/PageSkeletons"
import { CreditKpiBand } from "../CreditKpiBand"
import { CreditLedgerView } from "../CreditLedgerView"
import { CreditStatementView } from "../CreditStatementView"
import { CreditWalletCard } from "../CreditWalletCard"
import type { TDateRange } from "../date-range"
import type { IProps } from "./interfaces"

/** A per-section error tile — replaces just the section whose query failed. */
function SectionError({ message, onRetry }: { message: string; onRetry: () => void }): React.JSX.Element {
  return (
    <EmptyState
      tone="danger"
      icon={<IconAlertTriangle size={28} />}
      title={<Trans>Couldn't load this</Trans>}
      description={message}
      action={
        <Button variant="light" color="brand" leftSection={<IconRefresh size={16} />} onClick={onRetry}>
          <Trans>Try again</Trans>
        </Button>
      }
    />
  )
}

/**
 * The tenant credits page — pure presentation. The smart route wrapper
 * (`usage.tsx`) owns the wallet / statement / ledger queries, the date range,
 * pagination and error extraction; this view composes the story: a health KPI
 * band, where the credits went, what's in the wallet, and the activity ledger.
 * Each section renders its own error tile so one failed query never blanks the
 * page. CREDITS ONLY — there is no money in any prop or anything it renders.
 */
const ACTIVITY_PAGE_SIZE = 10

export const CreditsView = ({
  wallet,
  enforcementEnabled,
  statement,
  transactions,
  isLoading,
  walletError,
  statementError,
  transactionsError,
  onRetry,
  range,
  periodFrom,
  periodTo,
  now,
  onRangeChange,
  onBuyCredits,
  activityPage,
  onTransactionsPageChange,
}: IProps): React.JSX.Element => {
  const { i18n } = useLingui()

  if (isLoading && !wallet && !walletError) {
    return (
      <Container size="lg" py="xl">
        <PageHeaderSkeleton />
        <KpiRowSkeleton count={4} />
        <SimpleGrid cols={{ base: 1, md: 2 }} mb="lg">
          <TableSkeleton rows={4} cols={3} />
          <TableSkeleton rows={4} cols={2} />
        </SimpleGrid>
        <TableSkeleton rows={5} cols={5} />
      </Container>
    )
  }

  const rangeSelect = (
    <Select
      value={range}
      onChange={(v) => v && onRangeChange(v as TDateRange)}
      data={[
        { value: "mtd", label: i18n._(msg`This month`) },
        { value: "7d", label: i18n._(msg`Last 7 days`) },
        { value: "30d", label: i18n._(msg`Last 30 days`) },
        { value: "90d", label: i18n._(msg`Last 90 days`) },
      ]}
      w={150}
      allowDeselect={false}
      aria-label={i18n._(msg`Statement period`)}
    />
  )

  return (
    <Container size="lg" py="xl">
      <Group justify="space-between" align="flex-start" mb="lg" wrap="wrap" gap="md">
        <Stack gap={2}>
          <Title order={2}>
            <Trans>Credits</Trans>
          </Title>
          <Text c="dimmed" size="sm">
            <Trans>Your workspace wallet — grants, packs and what they were spent on.</Trans>
          </Text>
        </Stack>
        <Group gap="sm">
          {rangeSelect}
          <Button color="brand" leftSection={<IconShoppingCart size={16} />} onClick={onBuyCredits}>
            <Trans>Buy credits</Trans>
          </Button>
        </Group>
      </Group>

      <Stack gap="lg">
        {walletError ? (
          <SectionError message={walletError} onRetry={onRetry} />
        ) : (
          <CreditKpiBand
            wallet={wallet}
            enforcementEnabled={enforcementEnabled}
            statement={statement}
            periodFrom={periodFrom}
            periodTo={periodTo}
            now={now}
          />
        )}

        <Grid gutter="lg">
          <Grid.Col span={{ base: 12, md: 7 }}>
            {statementError ? (
              <SectionError message={statementError} onRetry={onRetry} />
            ) : (
              <CreditStatementView statement={statement} />
            )}
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 5 }}>
            {walletError ? (
              <SectionError message={walletError} onRetry={onRetry} />
            ) : (
              <CreditWalletCard wallet={wallet} enforcementEnabled={enforcementEnabled} now={now} />
            )}
          </Grid.Col>
        </Grid>

        {transactionsError ? (
          <SectionError message={transactionsError} onRetry={onRetry} />
        ) : (
          <CreditLedgerView
            ledgerRows={transactions?.rows ?? []}
            usageRows={statement?.byMeeting ?? []}
            page={activityPage}
            pageSize={ACTIVITY_PAGE_SIZE}
            isLoading={isLoading}
            onPageChange={onTransactionsPageChange}
          />
        )}
      </Stack>
    </Container>
  )
}
